import { CALENDAR_MIN_SLOT_MIN, type PlacedCalendarPiece, type ScheduleEntry } from "@edu/shared";
import { dayShort, firstLetter, minutesToTime, timeText } from "./common.ts";
import { EntryKind } from "./EntryKind.tsx";
import { SelfBookedIcon } from "../shell/icons.tsx";
import { draggablePiece } from "./entry-drag.ts";
import { OpeningBox } from "./OpeningBox.tsx";
import type { GridOpening } from "./openings.ts";
import type { DragGhost } from "./use-calendar-drag.ts";

/**
 * Одна колонка календаря: день, его фон, его занятия и призрак под курсором.
 *
 * Своим файлом с 10.09.2026: `CalendarGrid` стоял на 370 строках при потолке в
 * 400, а сам компонент на 262 при потолке в 150 (раздел 9 CLAUDE.md), и жест
 * переноса туда не влезал. Граница разреза проходит по смыслу: сетка это шапка,
 * шкала часов и общий диапазон, колонка это всё, что происходит внутри дня.
 *
 * Ничего нового по сравнению с прежним телом цикла здесь нет, кроме двух вещей
 * жеста: свойства взятия на коробке занятия и свойства приёма на получасовых
 * кнопках фона.
 */

export interface CalendarDayProps {
  date: string;
  today: boolean;
  /** Зона СМОТРЯЩЕГО: и колонки, и минуты считаются только в ней. */
  zone: string;
  /** Куски занятий этого дня, уже разложенные по дорожкам. */
  pieces: readonly PlacedCalendarPiece[];
  /** Строки сетки по ключу: кусок несёт только ключ. */
  byKey: ReadonlyMap<string, ScheduleEntry>;
  /** Окна записи по ключу: куски окон едут в общей раскладке рядом с занятиями. */
  openingsByKey: ReadonlyMap<string, GridOpening>;
  onSelectOpening: (opening: GridOpening, anchor: HTMLElement) => void;
  /** Диапазон часов календаря, общий для всех колонок. */
  range: { fromMin: number; toMin: number };
  /** Начала получасовых строк фона, минуты от местной полуночи. */
  rows: readonly number[];
  /** Минуты отметки «сейчас», либо `null`, если сегодня не эта колонка. */
  nowMin: number | null;
  /**
   * Высота приглушённого прошлого строкой для стиля, либо `null`, если день ещё
   * весь впереди. Считает сетка: там известен и сегодняшний день, и минута.
   */
  past: string | null;
  chatDots: ReadonlySet<string>;
  selectedKey: string | null;
  onSelect: (entry: ScheduleEntry, anchor: HTMLElement) => void;
  onEmpty: ((date: string, time: string) => void) | null;
  /** Призрак занятия в руке. Приезжает только той колонке, куда целятся. */
  ghost: DragGhost | null;
  boxProps: (entry: ScheduleEntry, piece: { startMin: number; endMin: number }) => object;
}

/** Отступ от верха колонки строкой для `calc`, в единицах часа. */
function offset(minutes: number, fromMin: number): string {
  return `calc(${String((minutes - fromMin) / 60)} * var(--cal-hour))`;
}

export function CalendarDay(props: CalendarDayProps) {
  const { date, range, zone } = props;
  const nowMin = props.nowMin;
  const onEmpty = props.onEmpty;

  return (
    <div className="cal__col" data-today={props.today}>
      {/*
        Прошлое приглушено, как в Outlook: у прошедшего дня вся колонка, у
        сегодняшнего то, что выше отметки. Это не украшение, а ответ на вопрос
        «что дальше»: без него глаз каждый раз заново ищет границу между тем, что
        уже было, и тем, что ещё будет.

        ⚠️ Слой не ловит нажатия (`pointer-events: none` в стилях). Завести
        занятие задним числом можно и нужно: перенос прошедшего занятия и разбор
        задним числом это обычная работа, а слой, съедающий нажатия, отнял бы у
        прошлого половину календаря
      */}
      {props.past !== null && (
        <div className="cal__past" aria-hidden="true" style={{ height: props.past }} />
      )}

      {/*
        Фон колонки. Кнопки, а не один слой с координатой нажатия: время
        получается точным, а не выведенным из пикселей, и экран остаётся
        проходимым с клавиатуры. Они же и цели жеста: дату с получасом клетка
        несёт на себе (`data-date`, `data-min`), и жест читает их у той клетки,
        которая оказалась под курсором. Обработчиков приёма на клетке нет:
        нативного перетаскивания у нас не осталось (шапка `use-calendar-drag.ts`)
      */}
      {props.rows.map((m) =>
        onEmpty === null ? (
          // Ученику фон это просто разлиновка: он расписание смотрит
          <div
            key={m}
            className="cal__slot"
            data-hour={m % 60 === 0}
            data-date={date}
            data-min={m}
          />
        ) : (
          <button
            key={m}
            type="button"
            className="cal__slot"
            data-hour={m % 60 === 0}
            /*
              ⚠️ Имя на время жеста НЕ подменяется: подменённое имя обещало бы
              читалке действие, которого у неё нет. Перетаскивания с клавиатуры
              нет и не будет, клавиатурный путь переноса это форма в карточке
            */
            aria-label={`Добавить занятие: ${dayShort(date)}, ${minutesToTime(m)}`}
            data-date={date}
            data-min={m}
            onClick={() => onEmpty(date, minutesToTime(m))}
          />
        ),
      )}

      {props.pieces.map((p) => {
        const drawnEnd = Math.max(p.endMin, p.startMin + CALENDAR_MIN_SLOT_MIN);
        const opening = props.openingsByKey.get(p.entryKey);
        if (opening !== undefined) {
          return (
            <OpeningBox
              key={p.key}
              opening={opening}
              piece={p}
              zone={zone}
              top={offset(p.startMin, range.fromMin)}
              height={`calc(${String((drawnEnd - p.startMin) / 60)} * var(--cal-hour) - 2px)`}
              selected={opening.key === props.selectedKey}
              onSelect={props.onSelectOpening}
            />
          );
        }
        const entry = props.byKey.get(p.entryKey);
        if (entry === undefined) return null;
        /**
         * Отменённое и уехавшее занятие места не занимает (`lanes: 0`), и
         * рисуется узкой полосой у края колонки. Иначе отмена ухудшала бы
         * картину вместо того, чтобы её освобождать.
         */
        const width = p.lanes === 0 ? 0.18 : 1 / p.lanes;
        const left = p.lanes === 0 ? 0 : p.lane / p.lanes;
        return (
          <button
            key={p.key}
            type="button"
            className="cal__ev"
            /*
              Ключ строки в разметке. Нужен ровно одному читателю и ровно для
              одного случая: занятие, названное АДРЕСОМ (`?lesson=`),
              раскрывается без нажатия, то есть коробку, от которой карточка
              считает своё место, взять неоткуда, кроме как из DOM. Нажатию он не
              нужен: там коробка приезжает событием. Ключ, а не ключ куска: у
              занятия через полночь кусков два, и якорем обязан быть первый, в
              дне его начала
            */
            data-entry={entry.key}
            data-state={entry.state ?? "detached"}
            data-thin={p.lanes === 0}
            data-selected={entry.key === props.selectedKey}
            style={{
              top: offset(p.startMin, range.fromMin),
              height: `calc(${String((drawnEnd - p.startMin) / 60)} * var(--cal-hour) - 2px)`,
              left: `calc(${String(left * 100)}% + 2px)`,
              width: `calc(${String(width * 100)}% - 4px)`,
            }}
            aria-haspopup="dialog"
            aria-expanded={entry.key === props.selectedKey}
            onClick={(e) => props.onSelect(entry, e.currentTarget)}
            {...(draggablePiece(entry, p) ? props.boxProps(entry, p) : {})}
          >
            <span className="cal__ev-time">{p.continues ? "↳" : timeText(entry.at, zone)}</span>
            <span className="cal__ev-who">
              <EntryKind entry={entry} />
              {/* Урок из записи ученика на открытое время (3.2 проекта). Значком:
                  слово в коробку шириной в полколонки не влезает */}
              {entry.selfBooked === true && (
                <span className="kind">
                  <SelfBookedIcon className="kind__ico" label="Ученик записался сам" />
                </span>
              )}
              {entry.counterpartName ?? firstLetter(entry.counterpartName)}
            </span>
            {/*
              Непрочитанное в чате занятия: точка в НИЖНЕМ углу коробки, а не в
              ряду отметок сверху.

              ⚠️ **Своё место обязательно, и это не эстетика.** В ряду отметок
              предупреждающим цветом уже горит «не пришёл», и вторая точка того же
              цвета в двух пикселях от неё читалась бы как ещё одна отметка об
              ученике. Здесь же она отвечает на другой вопрос и стоит там, где
              отметок нет.

              ⚠️ Узлом `span`, а не `i`, намеренно: у полоски отменённого занятия
              (`data-thin`) правило гасит все `span`, и точка на ней исчезает
              сама. Точка на striped-полоске в три пикселя шириной была бы
              мусором, а прочитать такой чат человек всё равно приходит в
              карточку.
            */}
            {props.chatDots.has(entry.key) && (
              <>
                <span className="cal__ev-chat" aria-hidden="true" />
                {/* Читалке ни цвет, ни угол не видны вовсе, поэтому та же новость
                    говорится ей словами */}
                <span className="offscreen">есть новые сообщения</span>
              </>
            )}
            {/*
              Учёт в самой сетке: точка в углу коробки, а не слово. Место у
              коробки в 45 минут это три строки, четвёртой там нет, а вопрос
              «оплачено ли» задают взглядом по всей неделе сразу.

              ⚠️ Точка есть ТОЛЬКО когда учёт включён: полей `paid` и
              `attendance` в ответе иначе нет вовсе, и это отличается от
              `paid: false`, то есть от «не отмечено». Ученику сервер их не
              присылает ни при каких обстоятельствах.

              ⚠️ Цвет тревоги здесь не используется: неотмеченное это ПУСТОЙ
              кружок, а не красный. Решение владельца 21.08.2026, и оно про
              смысл: преподаватель мог знать об оплате и просто не отметить.
            */}
            {(entry.paid !== undefined || entry.attendance !== undefined) && (
              <span className="cal__ev-marks" aria-hidden="true">
                {entry.attendance !== undefined && (
                  <i className="dot" data-mark={entry.attendance ?? "unset"} />
                )}
                {entry.paid !== undefined && (
                  <i className="dot" data-mark={entry.paid ? "paid" : "unset"} />
                )}
              </span>
            )}
          </button>
        );
      })}

      {/*
        Призрак занятия на будущем месте: ОТДЕЛЬНЫЙ СЛОЙ поверх занятий, а не
        подсветка кнопок фона.

        ⚠️ Подсветкой фона это сделать нельзя. Кнопки фона лежат НИЖЕ коробок
        занятий, а у коробки непрозрачный фон, значит подсветка «поставлю сразу
        за Петей» оказалась бы под Петей, то есть невидимой ровно в том случае,
        ради которого весь жест и затевался. Тем же слоем чинится ещё три вещи:
        подсветку не гасит затемнение прошлого (а в прошлое переносят чаще
        всего), подпись не режется клеткой шириной в 88 пикселей, и не мигает
        `dragleave` на каждом пересечении границы текста.

        ⚠️ Во всю ширину колонки, хотя после переезда занятие может поделить её с
        соседом (`packLanes`). Картинка «до» и «после» разойдётся, и это принято:
        показывать будущее деление дорожек значит считать раскладку дважды.
      */}
      {props.ghost !== null && (
        <div
          className="cal__ghost"
          aria-hidden="true"
          style={{
            // Хвост, вылезающий за границы видимых часов, прижимается к ним:
            // отрицательный отступ нарисовал бы призрак поверх шапки дня
            top: offset(Math.max(props.ghost.startMin, range.fromMin), range.fromMin),
            height: `calc(${String(
              Math.max(
                0,
                Math.min(props.ghost.endMin, range.toMin) -
                  Math.max(props.ghost.startMin, range.fromMin),
              ) / 60,
            )} * var(--cal-hour))`,
          }}
        >
          <span className="cal__ghost-time">{props.ghost.label}</span>
        </div>
      )}

      {/*
        Полоса «сейчас» рисуется ПОСЛЕ занятий и лежит поверх них: под коробкой
        идущего урока она была бы не видна ровно в ту минуту, ради которой
        заведена.

        `role="separator"` с подписью, а не `aria-hidden`: шкала часов читалке не
        адресована (время каждый занятие несёт своё), а вот граница «здесь
        сейчас» это единственное место, где текущее время на экране вообще
        написано
      */}
      {nowMin !== null && (
        <div
          className="cal__now"
          role="separator"
          aria-label={`сейчас ${minutesToTime(nowMin)}`}
          style={{ top: offset(nowMin, range.fromMin) }}
        />
      )}
    </div>
  );
}
